import { useMutation, useQueryClient } from '@tanstack/react-query';

import { roleService } from '@/services/role-service';
import { pollUntilCondition } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';
import { uuidSha1 } from '@/utils/uuid';

import { roleMutationKeys, roleQueryKeys } from './query-keys';

export type GrantRolePermissionArg =
  | { permission: string }
  | { permissionId: UUID; permission: string };

/**
 * Hook to grant a permission to a role.
 * Waits until the role permissions list reflects the new permission, then invalidates.
 */
export function useGrantRolePermission(tenantId?: UUID, roleId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: roleMutationKeys.grantRolePermission(tenantId, roleId),
    mutationFn: async (arg: GrantRolePermissionArg) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!roleId) throw new Error('Missing role id');

      const permission = arg.permission;
      const permissionId =
        'permissionId' in arg && arg.permissionId
          ? arg.permissionId
          : await uuidSha1(roleId, permission);

      await roleService.grantRolePermission(tenantId, roleId, permissionId, {
        permission,
      });

      await pollUntilCondition(async () => {
        let cursor: string | undefined;
        do {
          const page = await roleService.listRolePermissions(tenantId, roleId, {
            limit: 100,
            next: cursor,
            filter: permission,
          });
          if (
            page.items.some(
              (p) => p.permission === permission && p.granted === true
            )
          )
            return true;
          cursor = page.next ?? undefined;
        } while (cursor);
        return false;
      });

      return { permission, permissionId };
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: roleQueryKeys.permissionListByRole(tenantId, roleId),
        refetchType: 'all',
      });
    },
  });
}
